import { useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import { chatKeys } from '../../api/query-keys';
import { type MessagesInfiniteData, seedEmptyMessagesData } from './messages-cache';

/** Writes the chat query caches that streaming and thread creation update outside a query or mutation. */
export const useChatMessagesCache = () => {
    const queryClient = useQueryClient();

    return useMemo(
        () => ({
            getMessages: (threadId: string): MessagesInfiniteData | undefined =>
                queryClient.getQueryData<MessagesInfiniteData>(chatKeys.messages(threadId)),
            invalidateAllChats: (): void => {
                queryClient.invalidateQueries({ queryKey: chatKeys.all });
            },
            /**
             * Refreshes the thread row and the sidebar list without touching the message list:
             * refetching messages would discard whatever streamed in before the stream ended.
             */
            reconcileThreadAndList: (threadId: string): void => {
                queryClient.invalidateQueries({ queryKey: chatKeys.thread(threadId), exact: true });
                queryClient.invalidateQueries({ queryKey: chatKeys.threads(), exact: true });
            },
            /** Refetches the message list; only safe once the server has persisted the turn's end. */
            refetchMessages: (threadId: string): void => {
                queryClient.invalidateQueries({ queryKey: chatKeys.messages(threadId) });
            },
            seedEmptyMessages: (threadId: string): void => {
                queryClient.setQueryData<MessagesInfiniteData>(chatKeys.messages(threadId), seedEmptyMessagesData());
            },
            updateMessages: (
                threadId: string,
                updater: (data: MessagesInfiniteData | undefined) => MessagesInfiniteData | undefined,
            ): void => {
                queryClient.setQueriesData<MessagesInfiniteData>({ queryKey: chatKeys.messages(threadId) }, updater);
            },
        }),
        [queryClient],
    );
};
